import { useEffect, useState } from "react";

export const useLocalStorage = <T>(key: string, initialValue: T) => {
	const isLocalStorageAvailable = () => {
		try {
			const testKey = "__test_key__";

			localStorage.setItem(testKey, testKey);
			localStorage.removeItem(testKey);

			return true;
		} catch (e) {
			console.error("localStorage is not available:", e);

			return false;
		}
	};

	const [storedValue, setStoredValue] = useState<T>(() => {
		try {
			const item = globalThis.localStorage.getItem(key);

			return item ? JSON.parse(item) : initialValue;
		} catch (error) {
			console.error(error);

			return initialValue;
		}
	});

	if (!isLocalStorageAvailable()) {
		console.error(
			"localStorage is not available. Please check your browser settings or disable private browsing."
		);
	}

	const setValue = (value: T | ((val: T) => T)) => {
		try {
			const valueToStore = typeof value === "function" ? value(storedValue) : value;

			globalThis.localStorage.setItem(key, JSON.stringify(valueToStore));
			setStoredValue(valueToStore);
		} catch (error) {
			console.error(error);
		}
	};

	const removeValue = () => {
		try {
			setStoredValue(initialValue);
			globalThis.localStorage.removeItem(key);
		} catch (error) {
			console.error(error);
		}
	};

	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key === key) {
				try {
					setStoredValue(event.newValue ? JSON.parse(event.newValue) : initialValue);
				} catch (error) {
					console.error(error);
					setStoredValue(initialValue);
				}
			}
		};

		globalThis.addEventListener("storage", handleStorageChange);

		return () => globalThis.removeEventListener("storage", handleStorageChange);
	}, [key, initialValue]);

	return { storedValue, setValue, removeValue };
};

export const clearAllLocalStorage = () => {
	try {
		globalThis.localStorage.clear();
	} catch (error) {
		console.error(error);
	}
};
